import {Header} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import {spotlight} from "@mantine/spotlight";
import {useAuth0} from "@auth0/auth0-react";
import {
  BeakerIcon,
  BookOpenIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  InboxArrowDownIcon,
  MapIcon,
  QuestionMarkCircleIcon,
  QueueListIcon,
  RectangleStackIcon,
  TicketIcon,
  TruckIcon,
  UserIcon,
  ViewColumnsIcon,
} from "@heroicons/react/16/solid";
import {skipToken} from "@reduxjs/toolkit/query";
import {useCallback, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {
  changeRegionAndResetData,
  getCurrentRegion,
  RegionEntity,
  transportCategory,
  useGetContainerLevelSimulationQuery,
  useGetNoticeBadgeSummaryQuery
} from "@ots-frontend/ots-data";
import {getFormattedDateTime, useAccessibleRegions, useAuth, useChat} from "@ots-frontend/ots-ui";
import {
  FeedbackPrompt,
  ConnectedNoticeDrawer,
  ConnectedActiveShiftsTray,
  type ActiveShiftRow,
  OtsAppNavbar,
  OtsAppLogo,
  otsAppLogos,
  OtsAppNavbarItem,
  OtsRegionSwitcher,
  OtsStatusStripItem,
  OtsTransportCategorySwitcher,
  useOtsAppearanceMenuItems
} from "@ots-frontend/ots-ui-tw";
import {useGetPendingDispatchRequests} from "../../hooks/useGetPendingDispatchRequests";
import {useNavLayout} from "../../hooks/useNavLayout";
import {loadBaseData, logout} from "../../store/auth/actions";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useAppThunkDispatch} from "../../store/hooks/useAppThunkDispatch";
import {setAppCategory, setAppDate} from "../../store/ui/actions";
import {activeShiftFocusUrl} from "../../hooks/active-shift-navigation";
import {setMapFilterEntity, setMapFocusPoint} from "../../store/map/actions";
import {getAppCategory} from "../../store/ui/selectors";

const CATEGORIES = [transportCategory.water, transportCategory.oil];

/**
 * DispatchGo's navigation across the top, built the way the dashboard's is: a catalyst navbar with the
 * region switcher, destinations, and account menu, over a status strip.
 */
export const TopNavBar = () => {
  const appearance = useOtsAppearanceMenuItems();
  const [, setNavLayout] = useNavLayout();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const thunkDispatch = useAppThunkDispatch();
  const chat = useChat();
  const {user, hasAccess} = useAuth();
  const {isAuthenticated, logout: auth0Logout} = useAuth0();
  const appCategory = useAppSelector(getAppCategory);
  const currentRegion = useAppSelector<RegionEntity | undefined>(getCurrentRegion);
  const {regions, canSwitchRegion} = useAccessibleRegions();
  const [noticesOpened, notices] = useDisclosure(false);

  const canViewNotices = hasAccess("notice_view");
  const {data: noticeBadge} = useGetNoticeBadgeSummaryQuery(canViewNotices ? undefined : skipToken);
  const {dispatchRequests} = useGetPendingDispatchRequests();
  const {data: simulation, isError: simulationFailed} = useGetContainerLevelSimulationQuery(undefined, {pollingInterval: 5000});

  const pending = dispatchRequests.length;
  const canViewDvirs = hasAccess("dvir_view");
  // Six destinations; the rest sit one click down, grouped by what people come to do.
  const items = useMemo<OtsAppNavbarItem[]>(() => {
    const to = (path: string) => () => navigate(path);
    return [
      {id: "/driver-activity", label: "Driver Activity", icon: <TruckIcon/>, onClick: to("/driver-activity")},
      {id: "dispatching", label: "Dispatch", icon: <ClipboardDocumentCheckIcon/>, children: [
        {id: "/dispatch", label: "Dispatches", description: "Build and assign the day's work", icon: <ClipboardDocumentCheckIcon/>, onClick: to("/dispatch")},
        {id: "/requests", label: "Dispatch Requests", description: "Requests from operators and the field", icon: <InboxArrowDownIcon/>, onClick: to("/requests")},
        {id: "/requests/pending", label: "Pending Requests", description: "Waiting to be accepted", icon: <QueueListIcon/>, badgeCount: pending, onClick: to("/requests/pending")},
      ]},
      {id: "/tickets", label: "Tickets", icon: <TicketIcon/>, onClick: to("/tickets")},
      {id: "records", label: "Records", icon: <ClipboardDocumentListIcon/>, children: [
        {id: "/shifts", label: "Shifts", description: "Driver shifts and hours", icon: <ClockIcon/>, onClick: to("/shifts")},
        ...(canViewDvirs ? [{id: "/dvirs", label: "DVIRs", description: "Vehicle inspection reports", icon: <ClipboardDocumentListIcon/>, onClick: to("/dvirs")}] : []),
      ]},
      {id: "field", label: "Field", icon: <MapIcon/>, children: [
        {id: "/map", label: "Map", description: "Where drivers and sites are now", icon: <MapIcon/>, onClick: to("/map")},
        {id: "/container-status", label: "Container Status", description: "Tank levels and alerts", icon: <RectangleStackIcon/>, onClick: to("/container-status")},
      ]},
      {id: "/directory", label: "Directory", icon: <BookOpenIcon/>, onClick: to("/directory")},
    ];
  }, [navigate, pending, canViewDvirs]);
  // The longest matching path wins, so /requests/pending marks Pending Requests rather than Dispatch Requests.
  const activePath = items.flatMap(item => item.children?.map(child => child.id) ?? [item.id])
    .filter(path => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  const onRegionChange = useCallback((region: RegionEntity | undefined) => {
    if (region) thunkDispatch(changeRegionAndResetData(region.id, loadBaseData));
  }, [thunkDispatch]);

  const onLogout = useCallback(() => {
    thunkDispatch(logout());
    if (isAuthenticated) {
      try {
        auth0Logout({logoutParams: {returnTo: window.location.origin}});
      } catch (error) {
        console.error(error);
      }
    }
  }, [thunkDispatch, isAuthenticated, auth0Logout]);

  const onShiftMap = useCallback((row: ActiveShiftRow) => {
    if (!row.position) return;
    dispatch(setMapFilterEntity("PERSON"));
    dispatch(setMapFocusPoint({id: row.personId, displayName: row.driver,
      latLng: {latCoord: row.position.latitude, longCoord: row.position.longitude}}));
    navigate("/map");
  }, [dispatch, navigate]);
  const onShiftGrid = useCallback((destination: "dispatch" | "tickets", row: ActiveShiftRow) => {
    const now = Date.now();
    dispatch(setAppDate(now));
    navigate(activeShiftFocusUrl(destination, row, now));
  }, [dispatch, navigate]);

  // What the sidebar layout shows as header badges lives in the status strip here.
  const statusItems = useMemo<OtsStatusStripItem[]>(() => [
    ...(currentRegion ? [{id: "active-shifts", label: "Active shifts", content: <ConnectedActiveShiftsTray
      key={currentRegion.id} regionId={currentRegion.id} regionName={currentRegion.name}
      brand="dispatch-go" colorScheme="system" onViewMap={onShiftMap}
      onViewDispatch={row => onShiftGrid("dispatch", row)} onViewTickets={row => onShiftGrid("tickets", row)}/>}] : []),
    ...(!simulationFailed && simulation?.enabled ? [{
      id: "simulation",
      label: "Simulated Container Levels On",
      icon: <BeakerIcon/>,
      tone: "warning" as const,
      onClick: () => navigate("/directory"),
    }] : []),
    ...(dispatchRequests.length > 0 ? [{
      id: "pending-requests",
      label: `${dispatchRequests.length} Pending Dispatch Request${dispatchRequests.length === 1 ? "" : "s"}`,
      icon: <QueueListIcon/>,
      tooltip: "Open the pending dispatch requests",
      onClick: () => navigate("/requests/pending"),
    }] : []),
  ], [simulation?.enabled, simulationFailed, dispatchRequests.length, navigate, currentRegion, onShiftMap, onShiftGrid]);

  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : "Account";

  // The Mantine shell pads the page by the header's height, so it follows the bar's real height.
  const [height, setHeight] = useState(94);

  return (
    <>
      <Header height={height} p={0} withBorder={false} styles={{root: {backgroundColor: "transparent"}}}>
          <OtsAppNavbar
            brand="dispatch-go"
            colorScheme="system"
                        onHeightChange={setHeight}
                        pinKey="dispatch-go"
            logo={(
              <OtsAppLogo {...otsAppLogos["dispatch-go"]} onClick={() => navigate("/")}/>
            )}
            context={(
              <OtsTransportCategorySwitcher
                compact
                value={appCategory.code}
                options={CATEGORIES.map(category => ({code: category.code, label: category.displayValue.defaultMessage}))}
                onChange={(code) => {
                  const next = CATEGORIES.find(category => category.code === code);
                  if (next) dispatch(setAppCategory(next));
                }}/>
            )}
            start={currentRegion && (
              <OtsRegionSwitcher
                  compact
                  currentRegion={currentRegion}
                  regions={regions}
                  canSwitchRegion={canSwitchRegion}
                  onRegionChange={onRegionChange}
                variant="navbar"/>
            )}
            items={items}
            wideNavigation
            currentId={activePath}
            processes={{}}
            onSearch={() => spotlight.open()}
            notices={canViewNotices ? {count: noticeBadge?.activeCount, onClick: notices.open} : undefined}
            actions={chat.enabled ? [{id: "chat", label: "Chat", icon: <ChatBubbleLeftEllipsisIcon/>, badgeCount: chat.unread, onClick: () => navigate("/chat")}] : []}
            user={{name: userName, details: user?.emailAddress ? [user.emailAddress] : undefined}}
            userMenuItems={[
              {id: "profile", label: "My profile", icon: <UserIcon/>, onClick: () => navigate("/profile")},
              {id: "help", label: "Help & Support", icon: <QuestionMarkCircleIcon/>, onClick: () => navigate("/help")},
              ...appearance,
              {id: "layout", label: "Use side navigation", icon: <ViewColumnsIcon/>, onClick: () => setNavLayout("side"), separated: true},
            ]}
            end={<FeedbackPrompt placement="inline" launcherSize={32} brand="dispatch-go" colorScheme="system"/>}
            onSignOut={onLogout}
            status={{items: statusItems}}/>
      </Header>
      <ConnectedNoticeDrawer
        open={noticesOpened}
        onClose={notices.close}
        formatDateTime={getFormattedDateTime}
        brand="dispatch-go"
        colorScheme="system"/>
    </>
  );
};
